import { useTranslation } from "react-i18next";
import { IconCheckCircle } from "../../icons/IconCheckCircle/IconCheckCircle";
import { LeafMessageBox, LeafMessageBoxColor } from "../../leaf/LeafMessageBox/LeafMessageBox";
import { BaseTile } from "../BaseTile/BaseTile";
import { PeriodsProgressCircle } from "../PeriodsProgressCircle/PeriodsProgressCircle";
import { PeriodCircleLegend } from "../PeriodsProgressCircle/PeriodsCircleLegend/PeriodsCircleLegend";
import { getFullMonth, getPerformanceYearShort } from "../../util/period";
import { getNumberFormatter } from "../../util/format";
import { storedLocale } from "../../../components/Authentication/util";
import { useAppSelector } from "../../../store";
import { QUALIFICATION_STATUS, RequirementStatus } from "../../enums/incentive-enums";
import defaultContent from "../../../content/growthOrMaintainFQWithGPV";
import "./GrowthOrMaintainFQWithGPV.scss";

export type { RequirementStatus } from "../../enums/incentive-enums";

type FormattedValue = {
  Actual: number;
  Formatted: string;
};

export interface GrowthOrMaintainFQWithGPVProps {
  GrowthFQ: {
    Status: RequirementStatus;
    Met: boolean;
    Baseline: FormattedValue;
    YTDQCount: FormattedValue;
    Target: FormattedValue;
    Grow: FormattedValue;
    Tracking: {
      Growth: FormattedValue;
      Maintain: FormattedValue;
    };
  };
  AnnualGPV: {
    Status: RequirementStatus;
    Met: boolean;
    Target: FormattedValue;
    Baseline: FormattedValue;
    RequiredGPV: FormattedValue;
    YTDQCount: FormattedValue;
    Projected: FormattedValue;
  };
}

type StatusKey = "qualified" | "tracking" | "notQualified";

const getStatusMessage = (status: RequirementStatus, met: boolean): { key: StatusKey; color: LeafMessageBoxColor } => {
  if (met || status === QUALIFICATION_STATUS.QUALIFIED) {
    return { key: "qualified", color: LeafMessageBoxColor.GREEN };
  }
  if (status === QUALIFICATION_STATUS.TRACKING) {
    return { key: "tracking", color: LeafMessageBoxColor.BLUE };
  }
  return { key: "notQualified", color: LeafMessageBoxColor.ORANGE };
};

const isPositiveStatus = (status: RequirementStatus, met: boolean) =>
  met || status === QUALIFICATION_STATUS.QUALIFIED || status === QUALIFICATION_STATUS.TRACKING;

export const GrowthOrMaintainFQWithGPV = ({ GrowthFQ, AnnualGPV }: GrowthOrMaintainFQWithGPVProps) => {
  const { t } = useTranslation(["growthOrMaintainFQWithGPV"]);
  const { user } = useAppSelector((store) => store.boot);
  const { selectedPeriod } = useAppSelector((store) => store.period);
  const locale = storedLocale();

  const currentPYShort = getPerformanceYearShort(selectedPeriod);
  // FQ baseline is measured against the previous performance year (e.g. PY25 selected => PY24 baseline)
  const baselinePYShort = String(Number(currentPYShort) - 1);

  const growthMessage = getStatusMessage(GrowthFQ.Status, GrowthFQ.Met);
  const gpvMessage = getStatusMessage(AnnualGPV.Status, AnnualGPV.Met);

  const growthTrackingPositive = isPositiveStatus(GrowthFQ.Status, GrowthFQ.Met);
  const gpvTrackingPositive = isPositiveStatus(AnnualGPV.Status, AnnualGPV.Met);

  const gpvTitle = t("annualGPV.title", defaultContent.annualGPV.title, {
    target: AnnualGPV.Target.Formatted,
  });

  const numberFormatter = getNumberFormatter(locale, user.isoCountryCode, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  const formatCount = (value: number) => numberFormatter.format(value);

  const monthlyTarget = formatCount(Math.round(AnnualGPV.RequiredGPV.Actual / 12));

  const getIconTitle = (met: boolean): string =>
    String(
      t(
        met ? "accessibility.iconMet" : "accessibility.iconNotMet",
        met ? defaultContent.accessibility.iconMet : defaultContent.accessibility.iconNotMet,
      ),
    );

  const valueClass = (positive: boolean) =>
    positive ? "GrowthOrMaintainFQWithGPV__value--green" : "GrowthOrMaintainFQWithGPV__value--orange";

  const chartTitle = (
    <>
      <h3>PY{currentPYShort}</h3>
      <p>({getFullMonth(selectedPeriod, locale)})</p>
    </>
  );

  return (
    <div className="GrowthOrMaintainFQWithGPV">
      <BaseTile>
        <section className="GrowthOrMaintainFQWithGPV__inner">
          <div className="GrowthOrMaintainFQWithGPV__section">
            <div className="GrowthOrMaintainFQWithGPV__header">
              <IconCheckCircle color={GrowthFQ.Met ? "green" : "gray"} size="md" title={getIconTitle(GrowthFQ.Met)} />
              <h2 className="GrowthOrMaintainFQWithGPV__title">
                {t("growthFQ.title", defaultContent.growthFQ.title, { growth: GrowthFQ.Grow.Formatted })}
              </h2>
            </div>

            <LeafMessageBox color={growthMessage.color}>
              {t(`growthFQ.status.${growthMessage.key}`, defaultContent.growthFQ.status[growthMessage.key], {
                growth: GrowthFQ.Grow.Formatted,
              })}
            </LeafMessageBox>

            <dl className="GrowthOrMaintainFQWithGPV__kpis">
              <div className="GrowthOrMaintainFQWithGPV__kpiRow">
                <dt>{t("growthFQ.baseline", defaultContent.growthFQ.baseline, { perfYear: baselinePYShort })}</dt>
                <dd>{GrowthFQ.Baseline.Formatted}</dd>
              </div>
              <div className="GrowthOrMaintainFQWithGPV__kpiRow">
                <dt>{t("growthFQ.ytdFqs", defaultContent.growthFQ.ytdFqs as string)}</dt>
                <dd>{GrowthFQ.YTDQCount.Formatted}</dd>
              </div>
              <div className="GrowthOrMaintainFQWithGPV__kpiRow">
                <dt>
                  {t("growthFQ.growthTracking", defaultContent.growthFQ.growthTracking as string, {
                    growth: GrowthFQ?.Grow?.Formatted,
                  })}
                </dt>
                <dd className={valueClass(growthTrackingPositive)}>
                  {GrowthFQ.Tracking.Growth.Formatted} / {GrowthFQ.Target.Formatted}
                </dd>
              </div>
              <div className="GrowthOrMaintainFQWithGPV__kpiRow">
                <dt>{t("growthFQ.maintainTracking", defaultContent.growthFQ.maintainTracking as string)}</dt>
                <dd className={valueClass(growthTrackingPositive)}>
                  {GrowthFQ.Tracking.Maintain.Formatted} / {GrowthFQ.Baseline.Formatted}
                </dd>
              </div>
            </dl>
          </div>

          <div className="GrowthOrMaintainFQWithGPV__divider" aria-hidden="true">
            <span className="GrowthOrMaintainFQWithGPV__dividerLine" />
            <span className="GrowthOrMaintainFQWithGPV__dividerText">
              {t("divider", defaultContent.divider as string)}
            </span>
            <span className="GrowthOrMaintainFQWithGPV__dividerLine" />
          </div>

          <div className="GrowthOrMaintainFQWithGPV__section">
            <div className="GrowthOrMaintainFQWithGPV__header">
              <IconCheckCircle color={AnnualGPV.Met ? "green" : "gray"} size="md" title={getIconTitle(AnnualGPV.Met)} />
              <h2 className="GrowthOrMaintainFQWithGPV__title">{gpvTitle}</h2>
            </div>

            <LeafMessageBox color={gpvMessage.color}>
              {t(`annualGPV.status.${gpvMessage.key}`, defaultContent.annualGPV.status[gpvMessage.key] as string, {
                target: AnnualGPV.Target.Formatted,
              })}
            </LeafMessageBox>

            <dl className="GrowthOrMaintainFQWithGPV__kpis">
              <div className="GrowthOrMaintainFQWithGPV__kpiRow">
                <dt>{t("annualGPV.baseline", defaultContent.annualGPV.baseline as string)}</dt>
                <dd>{AnnualGPV.Baseline.Formatted}</dd>
              </div>
              <div className="GrowthOrMaintainFQWithGPV__kpiRow">
                <dt>
                  {t("annualGPV.requiredGPV", defaultContent.annualGPV.requiredGPV as string, {
                    perfYear: currentPYShort,
                  })}
                </dt>
                <dd>{AnnualGPV.RequiredGPV.Formatted}</dd>
              </div>
              <div className="GrowthOrMaintainFQWithGPV__kpiRow">
                <dt>{t("annualGPV.gpvYtd", defaultContent.annualGPV.gpvYtd as string)}</dt>
                <dd className={valueClass(gpvTrackingPositive)}>{AnnualGPV.YTDQCount.Formatted}</dd>
              </div>
              <div className="GrowthOrMaintainFQWithGPV__kpiRow">
                <dt>{t("annualGPV.projected", defaultContent.annualGPV.projected as string)}</dt>
                <dd className={valueClass(gpvTrackingPositive)}>{AnnualGPV.Projected.Formatted}</dd>
              </div>
            </dl>

            <p className="GrowthOrMaintainFQWithGPV__description">
              {t("annualGPV.description", defaultContent.annualGPV.description as string, { monthlyTarget })}
            </p>

            <div className="GrowthOrMaintainFQWithGPV__graph">
              <PeriodsProgressCircle
                period={selectedPeriod}
                targetAmount={{
                  actual: AnnualGPV.RequiredGPV.Actual,
                  formatted: AnnualGPV.RequiredGPV.Formatted,
                }}
                currentAmount={{
                  actual: AnnualGPV.YTDQCount.Actual,
                  formatted: AnnualGPV.YTDQCount.Formatted,
                }}
                tracking={gpvTrackingPositive}
                disabled={false}
                showLegend={false}
                chartTitle={chartTitle}
              />
              <PeriodCircleLegend disabled={false} />
            </div>
          </div>
        </section>
      </BaseTile>
    </div>
  );
};
